import React, { useState, useEffect } from 'react'
import { FileData } from '@/types'
import { FileInput } from '@/components/ui/FileInput'
import { Button } from '@/components/ui/Button'
import { toastError } from '@/utils/toast'
import { CertificateListDisplay } from './CertificateDisplay'
import { DuplicateCertificateModal } from './DuplicateCertificateModal'

interface IntermediateCertificatesSectionProps {
  initialCertificates?: FileData[]
  onSave: (certificates: FileData[]) => Promise<void>
  readOnly?: boolean
}

/**
 * Generates a unique filename by appending a number if the filename already exists
 */
const generateUniqueFilename = (filename: string, existingFilenames: string[]): string => {
  const lowerExisting = existingFilenames.map(f => f.toLowerCase())
  if (!lowerExisting.includes(filename.toLowerCase())) {
    return filename
  }

  const extension = filename.substring(filename.lastIndexOf('.'))
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'))
  let counter = 1
  let newFilename = `${nameWithoutExt}_${counter}${extension}`

  while (lowerExisting.includes(newFilename.toLowerCase())) {
    counter++
    newFilename = `${nameWithoutExt}_${counter}${extension}`
  }

  return newFilename
}

export const IntermediateCertificatesSection: React.FC<IntermediateCertificatesSectionProps> = ({
  initialCertificates,
  onSave,
  readOnly = false,
}) => {
  const [isSaving, setIsSaving] = useState(false)

  /** Editable working state */
  const [intermediateCertificates, setIntermediateCertificates] = useState<FileData[]>(
    initialCertificates || []
  )

  /** Last successfully submitted certificates */
  const [submittedCertificates, setSubmittedCertificates] = useState<FileData[]>(
    initialCertificates || []
  )

  /** Files selected in FileInput */
  const [intermediateCertificateFiles, setIntermediateCertificateFiles] = useState<File[]>([])

  /** Dirty flag */
  const [isDirty, setIsDirty] = useState(false)

  const [duplicateModal, setDuplicateModal] = useState<{
    filename: string
    newCertificate: FileData
    existingIndex: number
    fileIndex: number
  } | null>(null)

  const [pendingCertificates, setPendingCertificates] = useState<FileData[]>([])
  const [pendingFileIndices, setPendingFileIndices] = useState<number[]>([])
  const [removedIndices, setRemovedIndices] = useState<Set<number>>(new Set())

  useEffect(() => {
    if (initialCertificates) {
      setIntermediateCertificates(initialCertificates)
      setSubmittedCertificates(initialCertificates)
      setRemovedIndices(new Set())
      setIsDirty(false)
    }
  }, [initialCertificates])

  const markDirty = () => setIsDirty(true)

  const handleFileChange = async (files: FileList | null) => {
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    const startingFileIndex = intermediateCertificateFiles.length
    setIntermediateCertificateFiles(prev => [...prev, ...fileArray])

    const filePromises = fileArray.map(
      (file): Promise<FileData> =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            const base64 = (reader.result as string).split(',')[1]
            resolve({ filename: file.name, data: base64 })
          }
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
    )

    try {
      const newCertificates = await Promise.all(filePromises)
      const allCertificates = [...intermediateCertificates]
      const existingFilenames = allCertificates.map(cert => cert.filename || '')

      for (let i = 0; i < newCertificates.length; i++) {
        const newCert = newCertificates[i]
        const existingIndex = existingFilenames.findIndex(
          name => name.toLowerCase() === (newCert.filename || '').toLowerCase()
        )

        if (existingIndex !== -1) {
          setDuplicateModal({
            filename: newCert.filename || '',
            newCertificate: newCert,
            existingIndex,
            fileIndex: startingFileIndex + i,
          })

          setPendingCertificates(newCertificates.slice(i + 1))
          setPendingFileIndices(
            newCertificates.slice(i + 1).map((_, j) => startingFileIndex + i + 1 + j)
          )
          return
        }

        allCertificates.push(newCert)
        existingFilenames.push(newCert.filename || '')
      }

      setIntermediateCertificates(allCertificates)
      markDirty()
    } catch (error) {
      console.error(error)
      toastError('Erreur lors de la lecture des fichiers de certificats')
    }
  }

  const processPendingCertificates = (
    currentCertificates: FileData[],
    pending: FileData[],
    fileIndices: number[]
  ) => {
    const allCertificates = [...currentCertificates]
    const existingFilenames = allCertificates.map(cert => cert.filename || '')
    const updatedFiles = [...intermediateCertificateFiles]

    for (let i = 0; i < pending.length; i++) {
      const cert = pending[i]
      const fileIndex = fileIndices[i]

      const duplicate = existingFilenames.find(
        name => name.toLowerCase() === (cert.filename || '').toLowerCase()
      )

      if (!duplicate) {
        allCertificates.push(cert)
        existingFilenames.push(cert.filename || '')
      } else {
        const uniqueFilename = generateUniqueFilename(cert.filename || '', existingFilenames)
        allCertificates.push({ ...cert, filename: uniqueFilename })
        existingFilenames.push(uniqueFilename)

        if (fileIndex >= 0 && fileIndex < updatedFiles.length) {
          const originalFile = updatedFiles[fileIndex]
          updatedFiles[fileIndex] = new File([originalFile], uniqueFilename, {
            type: originalFile.type,
            lastModified: originalFile.lastModified,
          })
        }
      }
    }

    setIntermediateCertificates(allCertificates)
    setIntermediateCertificateFiles(updatedFiles)
    markDirty()
  }

  const handleDuplicateReplace = () => {
    if (!duplicateModal) return

    const updated = [...intermediateCertificates]
    updated[duplicateModal.existingIndex] = duplicateModal.newCertificate

    setIntermediateCertificates(updated)
    markDirty()

    if (pendingCertificates.length) {
      processPendingCertificates(updated, pendingCertificates, pendingFileIndices)
      setPendingCertificates([])
      setPendingFileIndices([])
    }

    setDuplicateModal(null)
  }

  const handleDuplicateKeepBoth = () => {
    if (!duplicateModal) return

    const existingFilenames = intermediateCertificates.map(c => c.filename || '')
    const uniqueFilename = generateUniqueFilename(
      duplicateModal.newCertificate.filename || '',
      existingFilenames
    )

    const updated = [
      ...intermediateCertificates,
      { ...duplicateModal.newCertificate, filename: uniqueFilename },
    ]

    setIntermediateCertificates(updated)
    
    // Update the File object in intermediateCertificateFiles with the new unique filename
    if (duplicateModal.fileIndex >= 0 && duplicateModal.fileIndex < intermediateCertificateFiles.length) {
      const updatedFiles = [...intermediateCertificateFiles]
      const originalFile = updatedFiles[duplicateModal.fileIndex]
      updatedFiles[duplicateModal.fileIndex] = new File([originalFile], uniqueFilename, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
      })
      setIntermediateCertificateFiles(updatedFiles)
    }
    
    markDirty()

    if (pendingCertificates.length) {
      processPendingCertificates(updated, pendingCertificates, pendingFileIndices)
      setPendingCertificates([])
      setPendingFileIndices([])
    }

    setDuplicateModal(null)
  }

  const handleDuplicateCancel = () => {
    if (pendingCertificates.length) {
      processPendingCertificates(intermediateCertificates, pendingCertificates, pendingFileIndices)
      setPendingCertificates([])
      setPendingFileIndices([])
    }
    setDuplicateModal(null)
  }

  const handleRemoveFile = (index: number) => {
    const updatedFiles = intermediateCertificateFiles.filter((_, i) => i !== index)
    setIntermediateCertificateFiles(updatedFiles)
    
    // Find the certificate that corresponds to this file
    const submittedFilenames = new Set(
      submittedCertificates.map(c => `${c.filename || ''}:${c.data || ''}`)
    )
    const newCertificates = intermediateCertificates.filter(
      c => !submittedFilenames.has(`${c.filename || ''}:${c.data || ''}`)
    )
    
    // Remove the certificate at the same relative position in new certificates
    const fileIndexInNew = index - (intermediateCertificateFiles.length - newCertificates.length)
    if (fileIndexInNew >= 0 && fileIndexInNew < newCertificates.length) {
      const updated = [
        ...submittedCertificates,
        ...newCertificates.filter((_, i) => i !== fileIndexInNew),
      ]
      setIntermediateCertificates(updated)
      markDirty()
    }
  }

  const handleRemoveExistingCertificate = (index: number) => {
    // index is in submittedCertificates, find the corresponding certificate in intermediateCertificates
    const certificateToRemove = submittedCertificates[index]
    if (!certificateToRemove) return

    // Find all occurrences of this certificate in intermediateCertificates and mark them as removed
    const updatedRemovedIndices = new Set(removedIndices)
    intermediateCertificates.forEach((cert, intermediateIndex) => {
      if (cert.filename === certificateToRemove.filename && cert.data === certificateToRemove.data) {
        updatedRemovedIndices.add(intermediateIndex)
      }
    })
    setRemovedIndices(updatedRemovedIndices)
    markDirty()
  }

  const handleRestoreCertificate = (index: number) => {
    // index is in submittedCertificates, find the corresponding certificate in intermediateCertificates
    const certificateToRestore = submittedCertificates[index]
    if (!certificateToRestore) return

    // Remove all occurrences of this certificate from removed indices
    const updatedRemovedIndices = new Set(removedIndices)
    intermediateCertificates.forEach((cert, intermediateIndex) => {
      if (cert.filename === certificateToRestore.filename && cert.data === certificateToRestore.data) {
        updatedRemovedIndices.delete(intermediateIndex)
      }
    })
    setRemovedIndices(updatedRemovedIndices)
    markDirty()
  }

  const handleReset = () => {
    setIntermediateCertificates([...submittedCertificates])
    setIntermediateCertificateFiles([])
    setPendingCertificates([])
    setPendingFileIndices([])
    setRemovedIndices(new Set())
    setDuplicateModal(null)
    setIsDirty(false)
  }

  const handleSubmit = async () => {
    setIsSaving(true)
    try {
      // Filter out removed certificates before saving
      const certificatesToSave = intermediateCertificates.filter((_, index) => !removedIndices.has(index))
      await onSave(certificatesToSave)
      setSubmittedCertificates(certificatesToSave)
      setIntermediateCertificates(certificatesToSave)
      setIntermediateCertificateFiles([])
      setRemovedIndices(new Set())
      setIsDirty(false)
    } catch (error) {
      toastError(
        error instanceof Error
          ? error.message
          : 'Erreur lors de la sauvegarde des certificats intermédiaires'
      )
    } finally {
      setIsSaving(false)
    }
  }

  // Only show submitted certificates in the display (not newly added ones)
  // Map removed indices from intermediateCertificates to submittedCertificates indices
  const displayCertificates = submittedCertificates.filter(c => c.filename && c.data)
  
  // Map removed indices: find indices in submittedCertificates that correspond to removed ones in intermediateCertificates
  const displayRemovedIndices = new Set<number>()
  intermediateCertificates.forEach((cert, intermediateIndex) => {
    if (removedIndices.has(intermediateIndex)) {
      // Find this certificate in submittedCertificates
      const submittedIndex = submittedCertificates.findIndex(
        sc => sc.filename === cert.filename && sc.data === cert.data
      )
      if (submittedIndex !== -1) {
        displayRemovedIndices.add(submittedIndex)
      }
    }
  })

  return (
    <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6">
      <h3 className="text-lg font-medium text-[var(--color-text-primary)] mb-4">
        Certificats intermédiaires
      </h3>
      <div className="space-y-6">
        <CertificateListDisplay
          certificates={displayCertificates}
          title="Certificats actuels"
          onRemove={handleRemoveExistingCertificate}
          showRemove={!readOnly}
          removedIndices={displayRemovedIndices}
          onRestore={handleRestoreCertificate}
        />

        <FileInput
          label="Ajouter des certificats intermédiaires"
          accept=".cer,.crt,.pem"
          multiple
          onChange={handleFileChange}
          selectedFiles={intermediateCertificateFiles}
          onFileRemove={handleRemoveFile}
          disabled={readOnly}
        />
      </div>
      {!readOnly && (
        <div className="flex justify-end gap-2 mt-4">
          {isDirty && (
            <Button variant="outline" onClick={handleReset} disabled={isSaving}>
              Réinitialiser
            </Button>
          )}

          <Button
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={!isDirty || intermediateCertificates.filter((_, i) => !removedIndices.has(i)).length === 0}
          >
            Enregistrer les certificats intermédiaires
          </Button>
        </div>
      )}

      {duplicateModal && (
        <DuplicateCertificateModal
          filename={duplicateModal.filename}
          onReplace={handleDuplicateReplace}
          onKeepBoth={handleDuplicateKeepBoth}
          onCancel={handleDuplicateCancel}
        />
      )}
    </div>
  )
}
